import { ec as EC } from 'elliptic';
const ec = new EC('p256');
function encB64(arr) {
    const bytes = arr instanceof Uint8Array ? arr : new Uint8Array(arr);
    return btoa(String.fromCharCode(...bytes));
}
function decB64(value) {
    return Uint8Array.from(atob(value), (char) => char.charCodeAt(0));
}
function asArrayBuffer(bytes) {
    return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
}
function bufferLikeToArrayBuffer(value) {
    return value instanceof Uint8Array ? asArrayBuffer(value) : value;
}
function encB64Url(bytes) {
    return encB64(new Uint8Array(bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}
function decB64Url(value) {
    let s = value.replace(/-/g, '+').replace(/_/g, '/');
    while (s.length % 4)
        s += '=';
    return decB64(s);
}
function keyPairToJwk(keyPair) {
    const pub = keyPair.getPublic();
    const priv = keyPair.getPrivate();
    const myPubJwk = {
        kty: 'EC',
        crv: 'P-256',
        x: encB64Url(pub.getX().toArray('be', 32)),
        y: encB64Url(pub.getY().toArray('be', 32)),
        ext: true
    };
    const myPrivJwk = {
        ...myPubJwk,
        d: encB64Url(priv.toArray('be', 32))
    };
    return { myPubJwk, myPrivJwk };
}
function privateJwkToKeyPair(jwk) {
    return ec.keyFromPrivate(decB64Url(jwk.d));
}
function publicJwkToKeyPair(jwk) {
    return ec.keyFromPublic(ec.curve.point(decB64Url(jwk.x), decB64Url(jwk.y)));
}
async function importAesKey(bytes, mode) {
    const material = mode === 'sha256' ? await crypto.subtle.digest('SHA-256', asArrayBuffer(bytes)) : asArrayBuffer(bytes);
    return crypto.subtle.importKey('raw', material, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}
export function generateKeypair() {
    return keyPairToJwk(ec.genKeyPair());
}
export function publicJwkFromPrivate(privateJwk) {
    const publicJwk = { ...privateJwk };
    delete publicJwk.d;
    return publicJwk;
}
export function keypairFromPrivate(privateJwk) {
    return {
        myPubJwk: publicJwkFromPrivate(privateJwk),
        myPrivJwk: privateJwk
    };
}
export async function sha256Hex(value) {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
    return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}
export async function calculatePrivateKeyHash(privateJwk) {
    return sha256Hex(JSON.stringify(privateJwk));
}
async function deriveKeyFromPassphrase(passphrase, salt, iterations) {
    const passKey = await crypto.subtle.importKey('raw', new TextEncoder().encode(passphrase), { name: 'PBKDF2' }, false, ['deriveKey']);
    return crypto.subtle.deriveKey({
        name: 'PBKDF2',
        salt: asArrayBuffer(salt),
        iterations,
        hash: 'SHA-256'
    }, passKey, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}
export async function exportPrivateKeyBackup(privateJwk, passphrase, iterations = 150000) {
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const key = await deriveKeyFromPassphrase(passphrase, salt, iterations);
    const plain = new TextEncoder().encode(JSON.stringify(privateJwk));
    const cipher = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: asArrayBuffer(iv) }, key, plain);
    return {
        v: 1,
        kdf: 'PBKDF2',
        hash: 'SHA-256',
        iter: iterations,
        salt: encB64(salt),
        iv: encB64(iv),
        data: encB64(cipher)
    };
}
export async function importPrivateKeyBackup(encrypted, passphrase) {
    const isWeb = typeof encrypted.salt === 'string' && typeof encrypted.iv === 'string' && typeof encrypted.data === 'string';
    const isLegacy = typeof encrypted.salt_b64 === 'string' && typeof encrypted.iv_b64 === 'string' && typeof encrypted.cipher_b64 === 'string';
    if (!isWeb && !isLegacy)
        throw new Error('Invalid encrypted key backup');
    const salt = decB64(String(isWeb ? encrypted.salt : encrypted.salt_b64));
    const iv = decB64(String(isWeb ? encrypted.iv : encrypted.iv_b64));
    const data = decB64(String(isWeb ? encrypted.data : encrypted.cipher_b64));
    const iterations = Number(encrypted.iter || (isWeb ? 150000 : 200000));
    const key = await deriveKeyFromPassphrase(passphrase, salt, iterations);
    const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: asArrayBuffer(iv) }, key, asArrayBuffer(data));
    return JSON.parse(new TextDecoder().decode(plain));
}
async function peerKey(myPrivate, peerPublic, mode = 'v2') {
    const shared = privateJwkToKeyPair(myPrivate).derive(publicJwkToKeyPair(peerPublic).getPublic());
    const bytes = new Uint8Array(shared.toArray('be', mode === 'v2' ? undefined : 32));
    return importAesKey(bytes, mode === 'legacy_raw' ? 'raw' : 'sha256');
}
export async function encryptText(myPrivate, peerId, peerPublic, text) {
    void peerId;
    const key = await peerKey(myPrivate, peerPublic);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: asArrayBuffer(iv) }, key, new TextEncoder().encode(text));
    return { iv: encB64(iv), data: encB64(ct) };
}
export async function encryptArrayBuffer(myPrivate, peerId, peerPublic, buffer) {
    void peerId;
    const key = await peerKey(myPrivate, peerPublic);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: asArrayBuffer(iv) }, key, bufferLikeToArrayBuffer(buffer));
    return { iv: encB64(iv), data: new Blob([ct]) };
}
export async function decryptToArrayBuffer(myPrivate, peerId, peerPublic, ivB64, blob) {
    void peerId;
    const modes = ['v2', 'v2_fixed', 'legacy_raw'];
    const cipher = await blob.arrayBuffer();
    let lastError;
    for (const mode of modes) {
        try {
            const key = await peerKey(myPrivate, peerPublic, mode);
            return crypto.subtle.decrypt({ name: 'AES-GCM', iv: asArrayBuffer(decB64(ivB64)) }, key, cipher);
        }
        catch (error) {
            lastError = error;
        }
    }
    throw lastError;
}
export async function decryptText(myPrivate, peerId, peerPublic, payload) {
    void peerId;
    const modes = ['v2', 'v2_fixed', 'legacy_raw'];
    let lastError;
    for (const mode of modes) {
        try {
            const key = await peerKey(myPrivate, peerPublic, mode);
            const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: asArrayBuffer(decB64(payload.iv)) }, key, asArrayBuffer(decB64(payload.data)));
            return new TextDecoder().decode(pt);
        }
        catch (error) {
            lastError = error;
        }
    }
    throw lastError;
}
async function groupKey(groupId) {
    const material = new TextEncoder().encode(`skychat-group:${groupId}`);
    const digest = await crypto.subtle.digest('SHA-256', material);
    return crypto.subtle.importKey('raw', digest, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}
export async function encryptTextForGroup(groupId, text) {
    const key = await groupKey(groupId);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: asArrayBuffer(iv) }, key, new TextEncoder().encode(text));
    return { iv: encB64(iv), data: encB64(ct) };
}
export async function decryptTextFromGroup(groupId, payload) {
    const key = await groupKey(groupId);
    const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: asArrayBuffer(decB64(payload.iv)) }, key, asArrayBuffer(decB64(payload.data)));
    return new TextDecoder().decode(pt);
}
export async function encryptArrayBufferForGroup(groupId, buffer) {
    const key = await groupKey(groupId);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: asArrayBuffer(iv) }, key, bufferLikeToArrayBuffer(buffer));
    return { iv: encB64(iv), data: new Blob([ct]) };
}
export async function decryptArrayBufferFromGroup(groupId, ivB64, blob) {
    const key = await groupKey(groupId);
    const cipher = await blob.arrayBuffer();
    return crypto.subtle.decrypt({ name: 'AES-GCM', iv: asArrayBuffer(decB64(ivB64)) }, key, cipher);
}
//# sourceMappingURL=crypto.js.map